import { Map as ImmutableMap, Set as ImmutableSet } from 'immutable';
import invariant from 'react-utils/invariant';
import Raven from 'raven-js';

import { RETINA_MULTIPLIER } from '../Constants';
import LookupRecord from '../Records/LookupRecord';
import { AVATAR_SOURCES, AvatarSource } from '../lib/AvatarSources';
import { AvatarCache, AvatarCacheEntry } from '../types/CacheEntries';
import { getUnauthedHubSpotUserAvatarUrl } from '../lib/getUnauthedHubSpotUserAvatarUrl';
import { DEFAULT_CACHE_ENTRY } from '../constants/cacheConstants';
import { classifyLookups, GroupedLookups } from './classifyLookups';
import { dimensionToAvatarSize } from './dimensionToAvatarSize';
import { mapRpcAvatarSource } from './mapRpcAvatarSource';
import { callResolveAvatarsByIdentifier } from './avatarRpcClient';
import type { AuthConfig } from './avatarRpcClient';
import defaultFetchAvatars from './defaultFetchAvatars';
import type {
  Avatar,
  AvatarSize,
} from '../../__generated__/chirp/com/hubspot/avatars/rpc/AvatarsRpc';

export const BASE_PATH = 'avatars/v1/';
export const BATCH_GET_PATH = 'avatars/batch';

type IdentifierType = string | number;

export interface AvatarResponse {
  [key: string]: {
    [identifier: string]: {
      uri?: string;
      queued?: boolean;
      source?: AvatarSource;
      fromDefault?: boolean;
    };
  };
}

interface NormalizedResponse {
  retry: ImmutableSet<LookupRecord>;
  updates: AvatarCache;
}

interface SizeParams {
  width: number;
  height: number;
}

interface RequestData {
  [key: string]: any;
  sizeParams?: SizeParams;
}

/**
 * Reduces the original lookups and the response into a Map of lookup => url.
 */
export function normalizeUpdates(
  lookups: ImmutableSet<LookupRecord>,
  response: AvatarResponse
): AvatarCache {
  return lookups.reduce((updates: AvatarCache, lookup: LookupRecord) => {
    const identifierName = lookup.getIdentifierName() as string;
    const identifier = lookup.getIdentifier() as IdentifierType;

    if (!identifierName || !identifier) {
      return updates.set(lookup, DEFAULT_CACHE_ENTRY);
    }

    const responseData = response[identifierName]?.[String(identifier)];
    if (responseData?.queued) {
      return updates;
    }

    return updates.set(lookup, {
      uri: responseData?.uri || null,
      source: responseData?.source || null,
    });
  }, ImmutableMap());
}

/**
 * Reduces the original lookups and the response into a Set of lookups that are
 * queued and should be retried.
 */
export function normalizeRetries(
  lookups: ImmutableSet<LookupRecord>,
  response: AvatarResponse
): ImmutableSet<LookupRecord> {
  return lookups.reduce(
    (retries: ImmutableSet<LookupRecord>, lookup: LookupRecord) => {
      const identifierName = lookup.getIdentifierName() as string;
      const identifier = lookup.getIdentifier() as IdentifierType;

      if (!identifierName || !identifier) {
        return retries;
      }

      const responseData = response[identifierName]?.[String(identifier)];
      if (!responseData?.queued) {
        return retries;
      }

      return retries.add(lookup);
    },
    ImmutableSet()
  );
}

export function normalizeError(
  lookups: ImmutableSet<LookupRecord>,
  response: AvatarResponse
): NormalizedResponse {
  return {
    retry: ImmutableSet(),
    updates: normalizeUpdates(lookups, response),
  };
}

export function normalizeResponse(
  lookups: ImmutableSet<LookupRecord>,
  response: AvatarResponse
): NormalizedResponse {
  return {
    retry: normalizeRetries(lookups, response),
    updates: normalizeUpdates(lookups, response),
  };
}

/**
 * Takes a list of lookups in transforms them to a JSON structure that the api
 * understands.
 */
export function toRequestData(
  lookups: ImmutableSet<LookupRecord>,
  dimension?: number
): RequestData {
  invariant(
    ImmutableSet.isSet(lookups),
    'expected `lookups` to be a Set but got `%s`',
    lookups
  );
  invariant(
    typeof dimension === 'undefined' || typeof dimension === 'number',
    'expected `dimension` to be undefined or a number but got `%s`',
    lookups
  );

  const requestDataWithoutDimensions = lookups.reduce(
    (
      builder: ImmutableMap<string, ImmutableSet<any>>,
      lookup: LookupRecord
    ) => {
      const identifierName = lookup.getIdentifierName() as string;
      if (!identifierName) {
        return builder;
      }

      const withoutKnown = builder.update(
        identifierName,
        (maybeSet: ImmutableSet<any> | undefined) => {
          const identifier = lookup.getIdentifier();
          return typeof maybeSet === 'undefined'
            ? ImmutableSet.of(identifier)
            : maybeSet.add(identifier);
        }
      );

      if (!lookup.isKnown()) {
        return withoutKnown;
      }

      const knownName = lookup.getKnownName() as string;
      if (!knownName) {
        return withoutKnown;
      }

      return withoutKnown.update(
        knownName,
        (maybeSet: ImmutableSet<any> | undefined) => {
          const knownObject = lookup.getKnownObject();
          return typeof maybeSet === 'undefined'
            ? ImmutableSet.of(knownObject)
            : maybeSet.add(knownObject);
        }
      );
    },
    ImmutableMap()
  );

  if (typeof dimension === 'number') {
    const doubleDimensionForRetina = dimension * RETINA_MULTIPLIER;
    const sizeParams: SizeParams = {
      width: doubleDimensionForRetina,
      height: doubleDimensionForRetina,
    };
    return {
      ...requestDataWithoutDimensions.toJS(),
      sizeParams,
    };
  }

  return requestDataWithoutDimensions.toJS();
}

export function getUnauthedAvatarData(requestData: RequestData): {
  unauthedData: AvatarResponse;
  hasRemainingLookups: boolean;
} {
  if (
    !('hubSpotUserEmails' in requestData) ||
    requestData.hubSpotUserEmails.length === 0
  )
    return { unauthedData: {}, hasRemainingLookups: true };

  const unauthedData = requestData.hubSpotUserEmails.reduce(
    (acc: AvatarResponse, email: string) => {
      acc.hubSpotUserEmails[email] = {
        fromDefault: false,
        queued: false,
        source: AVATAR_SOURCES.HUBSETTINGS_HUBSPOT_USER,
        uri: getUnauthedHubSpotUserAvatarUrl({
          hubSpotUserEmail: email,
          dimension: requestData.sizeParams?.width,
        }),
      };
      return acc;
    },
    { hubSpotUserEmails: {} }
  );

  delete requestData.hubSpotUserEmails;

  const hasRemainingLookups =
    Object.keys(requestData).filter((key) => key !== 'sizeParams').length > 0;

  return { unauthedData, hasRemainingLookups };
}

function buildAvatarCacheEntry(
  avatar: Avatar | undefined,
  lookup: LookupRecord
): AvatarCacheEntry {
  if (!avatar) {
    return DEFAULT_CACHE_ENTRY;
  }
  return {
    uri: avatar.uri || null,
    source: mapRpcAvatarSource(avatar.source, lookup.type),
  };
}

function mergeMaps(
  ...maps: Map<LookupRecord, AvatarCacheEntry>[]
): Map<LookupRecord, AvatarCacheEntry> {
  const merged = new Map<LookupRecord, AvatarCacheEntry>();
  for (const map of maps) {
    for (const [key, value] of map) {
      merged.set(key, value);
    }
  }
  return merged;
}

function toImmutableCache(
  map: Map<LookupRecord, AvatarCacheEntry>
): AvatarCache {
  return ImmutableMap(Array.from(map.entries()));
}

function resolveHubSpotUserEmails(
  entries: GroupedLookups['hubSpotUserEmails'],
  dimension: number | undefined
): Map<LookupRecord, AvatarCacheEntry> {
  return new Map(
    entries.map(({ lookup, email }) => [
      lookup,
      {
        uri: getUnauthedHubSpotUserAvatarUrl({
          hubSpotUserEmail: email,
          dimension,
        }),
        source: AVATAR_SOURCES.HUBSETTINGS_HUBSPOT_USER as AvatarSource,
      },
    ])
  );
}

function resolveAvatarsByIdentifier(
  entries: GroupedLookups['identifiers'],
  avatarSize: AvatarSize | undefined,
  trellisDefault: boolean,
  authConfig?: AuthConfig
): Promise<Map<LookupRecord, AvatarCacheEntry>> {
  if (entries.length === 0) {
    return Promise.resolve(new Map<LookupRecord, AvatarCacheEntry>());
  }

  return callResolveAvatarsByIdentifier(
    {
      identifiers: entries.map((entry) => entry.identifier),
      size: avatarSize,
      trellisDefault,
    },
    authConfig
  ).then((response) => {
    const avatarMap = response.avatarsByIdentifier;
    const results = new Map<LookupRecord, AvatarCacheEntry>();
    for (const { lookup, responseKey } of entries) {
      const avatar: Avatar | undefined = avatarMap[responseKey];
      results.set(lookup, buildAvatarCacheEntry(avatar, lookup));
    }
    return results;
  });
}

function buildDefaultFallbackMap(
  lookups: LookupRecord[]
): Map<LookupRecord, AvatarCacheEntry> {
  return new Map(lookups.map((l) => [l, DEFAULT_CACHE_ENTRY]));
}

function fetchAvatarsRpc(
  lookups: ImmutableSet<LookupRecord>,
  dimension?: number,
  trellisDefault?: boolean,
  authConfig?: AuthConfig
): Promise<NormalizedResponse> {
  const classified = classifyLookups(new Set(lookups.toArray()));
  const avatarSize = dimensionToAvatarSize(dimension);
  const useTrellisDefault = trellisDefault ?? false;
  const hubSpotUserResults = resolveHubSpotUserEmails(
    classified.hubSpotUserEmails,
    dimension
  );
  const unclassifiedFallback = buildDefaultFallbackMap(classified.unclassified);

  return resolveAvatarsByIdentifier(
    classified.identifiers,
    avatarSize,
    useTrellisDefault,
    authConfig
  )
    .catch((err) => {
      Raven.captureException(
        err instanceof Error
          ? err
          : new Error(`resolveAvatarsByIdentifier RPC failed: ${err}`),
        { extra: { identifierCount: classified.identifiers.length } }
      );
      return buildDefaultFallbackMap(
        classified.identifiers.map(({ lookup }) => lookup)
      );
    })
    .then((identifierResults) => ({
      retry: ImmutableSet<LookupRecord>(),
      updates: toImmutableCache(
        mergeMaps(unclassifiedFallback, hubSpotUserResults, identifierResults)
      ),
    }));
}

/**
 * Uses `fetcher` to retrieve and then normalize responses from the API.
 */
function fetchAvatarsApi(
  fetcher: (uri: string, data: object) => Promise<object>,
  portalId: number | undefined,
  lookups: ImmutableSet<LookupRecord>,
  dimension?: number,
  trellisDefault?: boolean
): Promise<NormalizedResponse> {
  invariant(
    typeof fetcher === 'function',
    'expected `fetcher` to be a function but got `%s`',
    fetcher
  );
  invariant(
    typeof portalId === 'number' || typeof portalId === 'undefined',
    'expected `portalId` to be a number or undefined but got `%s`',
    portalId
  );
  invariant(
    ImmutableSet.isSet(lookups),
    'expected `lookups` to be a Set but got `%s`',
    lookups
  );
  invariant(
    typeof dimension === 'number' || typeof dimension === 'undefined',
    'expected `dimension` to be a number or undefined but got `%s`',
    dimension
  );

  const queryParams = new URLSearchParams();
  if (typeof portalId !== 'undefined') {
    queryParams.set('portalId', String(portalId));
  }
  if (trellisDefault) {
    queryParams.set('trellisDefault', 'true');
  }
  const queryString = queryParams.toString()
    ? `?${queryParams.toString()}`
    : '';

  const requestData = toRequestData(lookups, dimension);

  const { unauthedData, hasRemainingLookups } =
    getUnauthedAvatarData(requestData);

  if (!hasRemainingLookups) {
    return Promise.resolve(normalizeResponse(lookups, unauthedData));
  }

  return fetcher(`${BASE_PATH}${BATCH_GET_PATH}${queryString}`, requestData)
    .then((response: unknown) => {
      if (typeof response !== 'object' || response === null) {
        throw new Error('Invalid response format');
      }

      return normalizeResponse(lookups, {
        ...response,
        ...unauthedData,
      } as AvatarResponse);
    })
    .catch(() => {
      // If the request fails, we return an empty responses
      // for all lookups which results in the use of the fallback avatar icon or image
      return normalizeError(lookups, unauthedData);
    });
}

export function fetchAvatars(
  fetcher: (uri: string, data: object) => Promise<object>,
  portalId: number | undefined,
  lookups: ImmutableSet<LookupRecord>,
  dimension?: number,
  trellisDefault?: boolean,
  authConfig?: AuthConfig
): Promise<NormalizedResponse> {
  // Custom fetchers replace the data source entirely, so bypass CHIRP and
  // use the REST path that honors the provided fetcher.
  if (fetcher !== defaultFetchAvatars) {
    return fetchAvatarsApi(
      fetcher,
      portalId,
      lookups,
      dimension,
      trellisDefault
    );
  }

  return fetchAvatarsRpc(lookups, dimension, trellisDefault, authConfig);
}
